import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RiCloseLine,
  RiUserLine,
  RiPaletteLine,
  RiPercentLine,
  RiCheckboxCircleFill,
  RiLoader4Line,
  RiMailLine,
  RiPhoneLine,
  RiFileTextLine,
  RiFilePdf2Fill,
  RiCalendar2Line,
} from '@remixicon/react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { resolveAssetUrl } from '@/utils';
import { buildOrgApprovalEmailHtml } from '@/utils/emailTemplates';
import Button from '@components/ui/Button';
import FeesTab from './FeesTab';
import { patchOrgInAdminCache } from '../utils';
import { ResetMfaButton } from '@/features/account/components';
import { MfaStepUpGateProvider } from '@/features/account/hooks/MfaStepUpGate';

// "Jan 15, 2026" — used for the owner's "Member since" row on the
// Account tab. Returns em-dash when the date is missing / unparseable
// so the row still displays cleanly.
const formatOwnerJoinDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return '—';
  }
};

/**
 * Fire the org-approval welcome email once the owner's status has
 * flipped to ACTIVE. Fire-and-forget from the approve mutations —
 * the DB write already committed at this point, so a failing
 * sendMessage never rolls the approval back; it just downgrades to
 * a warning toast asking the admin to resend manually.
 */
const sendOrgApprovalEmail = async ({ owner, organization }) => {
  if (!owner?.email) return;
  try {
    await apiClient.sendMessage({
      From: 'tools@trustjet.ai',
      To: owner.email,
      Cc: organization?.billing_email || '',
      ReplyTo: organization?.billing_email || '',
      Subject: `${organization?.name || 'Your organization'} has been approved on TrustJet`,
      HtmlBody: buildOrgApprovalEmailHtml({
        firstName: owner.first_name,
        lastName: owner.last_name,
        email: owner.email,
        organization,
      }),
    });
  } catch (err) {
    console.warn('org-approval email failed', err);
    toast.warning('Owner approved. Notification email could not be sent — resend manually.', {
      position: 'bottom-right',
    });
  }
};

const TABS = [
  { id: 'account', label: 'Account', icon: RiUserLine },
  { id: 'branding', label: 'Branding', icon: RiPaletteLine },
  { id: 'fees', label: 'Fees', icon: RiPercentLine },
];

// BE enum for organization.subscription_tier. Keep this list ordered
// from smallest to largest paying tier, with the sentinel values
// (INTERNAL / INACTIVE) grouped at the bottom.
const SUBSCRIPTION_TIERS = [
  'STARTER',
  'PROFESSIONAL',
  'ENTERPRISE',
  'INTERNAL',
  'INACTIVE',
];

const StatusBadge = ({ status }) => {
  const label = String(status || 'UNKNOWN').replaceAll('_', ' ');
  const map = {
    ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ONBOARDING_STARTED: 'bg-amber-50 text-amber-700 border-amber-200',
    ONBOARDING_COMPLETED: 'bg-amber-50 text-amber-800 border-amber-200',
    INACTIVE: 'bg-slate-100 text-slate-700 border-slate-200',
    SUSPENDED: 'bg-rose-50 text-rose-700 border-rose-200',
    ARCHIVED: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  const cls = map[String(status || '').toUpperCase()] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border ${cls}`}>
      {label}
    </span>
  );
};

const InfoRow = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-start gap-3 py-2">
    {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{label}</p>
      <p className={`text-sm text-slate-800 truncate ${mono ? 'font-mono text-[12px]' : ''}`}>
        {value || '—'}
      </p>
    </div>
  </div>
);

const DocumentLink = ({ label, path }) => {
  if (!path) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-100">
        <RiFileTextLine className="w-4 h-4 text-slate-400" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700 truncate">{label}</p>
          <p className="text-xs text-slate-400">Not uploaded</p>
        </div>
      </div>
    );
  }
  return (
    <a
      href={resolveAssetUrl(path)}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white border border-slate-200 hover:border-primary-300 hover:bg-primary-50/30 transition-colors"
    >
      <RiFilePdf2Fill className="w-5 h-5 text-red-500" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 font-medium truncate">{label}</p>
        <p className="text-xs text-primary-600">Open document ↗</p>
      </div>
    </a>
  );
};

/**
 * Modal shown when the admin clicks Approve owner on an org whose
 * subscription_tier is still INACTIVE (the default for fresh
 * signups). Forces a real tier pick so we never end up with an
 * ACTIVE owner sitting on an INACTIVE tenant.
 *
 * Runs the tier PATCH first, then the owner-status PATCH — that
 * way if the owner PATCH fails, the tier is at least set and the
 * admin can retry approve on the direct (non-dialog) path.
 */
const ApproveWithTierDialog = ({ organization, owner, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [selectedTier, setSelectedTier] = useState('');
  // Never show INACTIVE in the picker — the whole point is to
  // move OFF that value.
  const options = SUBSCRIPTION_TIERS.filter((t) => t !== 'INACTIVE');

  const combinedMutation = useMutation({
    mutationFn: async () => {
      await apiClient.updateOrganization(
        organization.organization_id,
        { subscription_tier: selectedTier },
      );
      await apiClient.updateUser(owner.userprofile_id, {
        status: 'ACTIVE',
        is_active: 'TRUE',
      });
    },
    onSuccess: () => {
      // Owner cache: flip status to ACTIVE right on the object so
      // the drawer's Approve banner disappears next render.
      const userKey = ['user', organization?.owned_by];
      queryClient.setQueryData(userKey, (prev) => ({
        ...(prev || {}),
        status: 'ACTIVE',
        is_active: 'TRUE',
      }));
      // Orgs-admin cache: patch the row's subscription_tier so the
      // table's Subscription column updates without a refetch.
      patchOrgInAdminCache(queryClient, organization.organization_id, (org) => ({
        ...org,
        subscription_tier: selectedTier,
      }));
      toast.success(
        `Owner approved and tier set to ${selectedTier}.`,
        { position: 'bottom-right' },
      );
      // Fire the welcome email with the tier already applied so the
      // recipient sees an ACTIVE organization on the header. Fire-
      // and-forget — never rolls back the approval on failure.
      sendOrgApprovalEmail({
        owner,
        organization: { ...organization, subscription_tier: selectedTier },
      });
      onSuccess?.();
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to approve owner.', {
        position: 'bottom-right',
      });
    },
  });

  const busy = combinedMutation.isPending;
  const canSave = !!selectedTier && !busy;
  const ownerFullName = [owner?.first_name, owner?.last_name].filter(Boolean).join(' ')
    || owner?.email
    || 'the owner';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      aria-modal
      role="dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Set a subscription tier
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              This organization is on <span className="font-mono">INACTIVE</span>.
              Pick a real tier before approving {ownerFullName}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-50"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2 block">
            Subscription tier <span className="text-rose-500">*</span>
          </label>
          <select
            value={selectedTier}
            onChange={(e) => setSelectedTier(e.target.value)}
            disabled={busy}
            className="w-full h-10 px-3 bg-white border-2 border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-primary-400 disabled:opacity-50"
          >
            <option value="" disabled>Choose a tier…</option>
            {options.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 px-5 pb-5">
          <Button
            type="button"
            variant="secondary"
            mode="stroke"
            className="!font-normal !border-slate-200 hover:!bg-slate-50 rounded-full"
            disabled={busy}
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            type="button"
            className="!font-normal bg-emerald-500 hover:bg-emerald-600 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canSave}
            onClick={() => combinedMutation.mutate()}
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <RiLoader4Line className="w-4 h-4 animate-spin" />
                Saving…
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <RiCheckboxCircleFill className="w-4 h-4" />
                Save
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AccountTab = ({ organization, ownerQuery, onApproved, onOrgUpdated }) => {
  const queryClient = useQueryClient();
  const owner = ownerQuery.data;
  const isLoading = ownerQuery.isLoading;
  const error = ownerQuery.error;

  const ownerStatus = String(owner?.status || '').toUpperCase();
  const canApprove = !!owner?.userprofile_id && ownerStatus !== 'ACTIVE';
  // If the org is still on the INACTIVE default (fresh signup), we
  // can't approve the owner in isolation — they'd end up ACTIVE on
  // an INACTIVE tenant. Route those approvals through a dialog that
  // forces a real tier pick alongside the approve.
  const orgIsInactive = String(organization?.subscription_tier || '').toUpperCase() === 'INACTIVE';
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const approveMutation = useMutation({
    mutationFn: () => apiClient.updateUser(owner.userprofile_id, {
      status: 'ACTIVE',
      is_active: 'TRUE',
    }),
    onSuccess: (updated) => {
      toast.success(`Owner approved — ${owner?.first_name || 'the owner'} is now active.`, {
        position: 'bottom-right',
      });
      // Write the new status straight into the cache so the banner
      // + button disappear immediately — the parent drawer's query
      // key is ['user', ownerId], so we mirror it here. Merge the
      // BE's response over what we had, then force status/is_active
      // to reflect the approval whether or not the BE echoes them.
      const key = ['user', organization?.owned_by];
      queryClient.setQueryData(key, (prev) => ({
        ...(prev || {}),
        ...(updated || {}),
        status: 'ACTIVE',
        is_active: 'TRUE',
      }));
      // Fire the welcome email — fire-and-forget, never blocks the
      // approve success flow.
      sendOrgApprovalEmail({ owner, organization });
      onApproved?.();
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to approve owner.', { position: 'bottom-right' });
    },
  });

  if (!organization?.owned_by) {
    return (
      <div className="p-6 text-sm text-slate-500 bg-amber-50/40 border border-amber-200 rounded-xl">
        No owner is currently set for this organization. Ask the org
        founder to complete their profile so <code>owned_by</code> gets
        stamped, or run the backfill script.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-slate-500">
        <RiLoader4Line className="w-4 h-4 animate-spin text-primary-500" />
        Loading owner details…
      </div>
    );
  }

  if (error || !owner) {
    return (
      <div className="p-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl">
        Could not load the owner ({error?.message || 'no profile returned'}).
      </div>
    );
  }

  const ownerFullName = [owner.first_name, owner.last_name].filter(Boolean).join(' ') || '—';

  return (
    <div className="space-y-5">
      <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-slate-900">{ownerFullName}</p>
            <p className="text-sm text-slate-500">{owner.email}</p>
          </div>
          <StatusBadge status={owner.status} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 mt-3">
          {/* Email is already surfaced at the top of the card — no
              duplicate InfoRow. Profile ID (UUID) replaced with a
              human-readable "Member since" so the row carries real
              signal for the super admin. */}
          <InfoRow icon={RiPhoneLine} label="Phone" value={owner.phone} />
          <InfoRow icon={RiUserLine} label="Role" value={owner.role} />
          <InfoRow
            icon={RiCalendar2Line}
            label="Member since"
            value={formatOwnerJoinDate(owner.created_at)}
          />
        </div>

        {canApprove && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-600 mb-3">
              This owner isn't active yet. Approving marks them as
              reviewed and lets them sign into the platform.
            </p>
            <Button
              type="button"
              className="!font-normal bg-emerald-500 hover:bg-emerald-600 rounded-full"
              disabled={approveMutation.isPending}
              onClick={() => {
                if (orgIsInactive) {
                  setApproveDialogOpen(true);
                  return;
                }
                approveMutation.mutate();
              }}
            >
              {approveMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <RiLoader4Line className="w-4 h-4 animate-spin" />
                  Approving…
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <RiCheckboxCircleFill className="w-4 h-4" />
                  Approve owner
                </span>
              )}
            </Button>
          </div>
        )}

        {/* Reset MFA — PROD-only support recovery for a locked-out
            owner. ResetMfaButton renders null in DEV / LOCAL and for
            non-admin viewers, so no gating needed at this call site. */}
        <div className="mt-4 pt-4 border-t border-slate-200">
          <ResetMfaButton
            userId={owner?.user_id}
            memberName={[owner?.first_name, owner?.last_name].filter(Boolean).join(' ') || owner?.email}
            mfaEnabled={owner?.mfa_enabled}
          />
        </div>
      </div>

      {approveDialogOpen && (
        <ApproveWithTierDialog
          organization={organization}
          owner={owner}
          onClose={() => setApproveDialogOpen(false)}
          onSuccess={() => {
            setApproveDialogOpen(false);
            onApproved?.();
            onOrgUpdated?.();
          }}
        />
      )}

      <SubscriptionSection
        organization={organization}
        onUpdated={onOrgUpdated}
      />

      <div>
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
          Documents
        </p>
        <div className="space-y-2">
          <DocumentLink label="Charter Contract" path={organization?.branding?.contract} />
          {organization?.type === 'OPERATOR' && (
            <>
              <DocumentLink label="DO85 Registration" path={organization?.branding?.do85Registration} />
              <DocumentLink label="Part 135 Certification" path={organization?.branding?.part135Certification} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Super-admin control for `organization.subscription_tier`. Change
// the dropdown → the Save button lights up → PATCH the org → the
// orgs-admin list refetches so the row's tier column updates too.
// Uses `onUpdated` (from the drawer) rather than an internal cache
// mutation so we don't have to reach into the useInfiniteQuery
// shape (pages: [[...], [...]]).
const SubscriptionSection = ({ organization, onUpdated }) => {
  const queryClient = useQueryClient();
  const currentTier = organization?.subscription_tier || '';
  const [selectedTier, setSelectedTier] = useState(currentTier);

  // Reset the dropdown whenever the drawer opens a different org
  // (or the underlying org refetches with a new tier).
  useEffect(() => {
    setSelectedTier(currentTier);
  }, [currentTier, organization?.organization_id]);

  const dirty = !!selectedTier && selectedTier !== currentTier;

  const mutation = useMutation({
    mutationFn: () => apiClient.updateOrganization(
      organization.organization_id,
      { subscription_tier: selectedTier },
    ),
    onSuccess: () => {
      toast.success(`Subscription tier updated to ${selectedTier}.`, {
        position: 'bottom-right',
      });
      // Patch the org row inside the orgs-admin infinite cache so
      // the table's Subscription column and the drawer's derived
      // `organization` prop both update without a network refetch.
      patchOrgInAdminCache(queryClient, organization.organization_id, (org) => ({
        ...org,
        subscription_tier: selectedTier,
      }));
      onUpdated?.();
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to update subscription tier.', {
        position: 'bottom-right',
      });
      // Roll back the dropdown so it matches the persisted tier.
      setSelectedTier(currentTier);
    },
  });

  return (
    <div className="p-4 rounded-2xl border border-slate-100 bg-white">
      <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-3">
        Subscription tier
      </p>
      <div className="flex items-center gap-3">
        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value)}
          disabled={mutation.isPending}
          className="h-10 px-3 flex-1 bg-white border-2 border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-primary-400 disabled:opacity-50"
        >
          {!SUBSCRIPTION_TIERS.includes(currentTier) && currentTier && (
            <option value={currentTier}>{currentTier} (unrecognised)</option>
          )}
          {SUBSCRIPTION_TIERS.map((tier) => (
            <option key={tier} value={tier}>{tier}</option>
          ))}
        </select>
        <Button
          type="button"
          className="!font-normal bg-primary-500 hover:bg-primary-600 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <RiLoader4Line className="w-4 h-4 animate-spin" />
              Saving…
            </span>
          ) : 'Save'}
        </Button>
      </div>
    </div>
  );
};

const BrandingTab = ({ organization }) => {
  const branding = organization?.branding || {};
  const settings = organization?.settings || {};
  const logoUrl = resolveAssetUrl(branding.logo);

  return (
    <div className="space-y-5">
      <div className="p-4 rounded-2xl border border-slate-100 bg-white">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-3">
          Logo
        </p>
        <div
          className="w-full h-32 rounded-xl flex items-center justify-center border border-slate-100"
          style={{ backgroundColor: branding.color || '#F3F4F6' }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${organization?.name || 'Organization'} logo`}
              className="max-h-24 max-w-[80%] object-contain"
            />
          ) : (
            <span className="text-sm text-white/80">No logo uploaded</span>
          )}
        </div>
      </div>

      <div className="p-4 rounded-2xl border border-slate-100 bg-white">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-3">
          Brand color
        </p>
        <div className="flex items-center gap-3">
          <span
            className="w-10 h-10 rounded-lg border border-slate-200"
            style={{ backgroundColor: branding.color || '#F3F4F6' }}
          />
          <span className="font-mono text-sm text-slate-700">
            {branding.color || '—'}
          </span>
        </div>
      </div>

      <div className="p-4 rounded-2xl border border-slate-100 bg-white">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-3">
          Company
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <InfoRow label="Name" value={organization?.name} />
          <InfoRow label="Type" value={organization?.type} />
          <InfoRow label="Billing email" value={organization?.billing_email} />
          <InfoRow label="Address" value={organization?.address?.street} />
          <InfoRow label="POC title" value={settings.pocTitle} />
          <InfoRow label="Sole prop." value={settings.isSole ? 'Yes' : 'No'} />
          <InfoRow label="Tax ID" value={organization?.tax_id} />
        </div>
      </div>
    </div>
  );
};

const OrganizationDrawerInner = ({ organization, open, onClose, onOwnerApproved, onOrgUpdated }) => {
  const [activeTab, setActiveTab] = useState('account');

  useEffect(() => {
    if (open) setActiveTab('account');
  }, [open, organization?.organization_id]);

  const ownerId = organization?.owned_by;

  // Lazy-fetch owner details only when the drawer opens on an org
  // whose `owned_by` is set. Keeps the list view fast — no N+1.
  // `owned_by` holds the userprofile_id, so we hit
  // GET /users/{userprofile_id} directly via getUser (not the
  // by-user-id variant, which resolves a Supabase auth user id).
  const ownerQuery = useQuery({
    queryKey: ['user', ownerId],
    queryFn: () => apiClient.getUser(ownerId),
    enabled: open && !!ownerId,
    retry: 1,
  });

  const drawerTitle = useMemo(() => organization?.name || 'Organization', [organization]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <aside className="fixed top-0 right-0 h-full w-full sm:w-[520px] bg-white z-50 shadow-2xl flex flex-col">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
              Organization
            </p>
            <h2 className="text-xl font-bold text-slate-900 truncate">{drawerTitle}</h2>
            {organization?.type && (
              <p className="text-xs text-slate-500 mt-1">{organization.type} · {organization.subscription_tier || '—'}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-1 px-3 pt-3 border-b border-slate-100 bg-white">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                  active
                    ? 'border-primary-500 text-primary-700 font-semibold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'account' && (
            <AccountTab
              organization={organization}
              ownerQuery={ownerQuery}
              onApproved={onOwnerApproved}
              onOrgUpdated={onOrgUpdated}
            />
          )}
          {activeTab === 'branding' && (
            <BrandingTab organization={organization} />
          )}
          {activeTab === 'fees' && (
            <FeesTab organization={organization} open={open} />
          )}
        </div>
      </aside>
    </>
  );
};

// Wrap in the MFA step-up gate so ResetMfaButton in the owner card
// still runs a TOTP challenge before firing the destructive PATCH —
// same shared component that gates the Account Settings surface.
// Provider mounts / unmounts with the drawer, so closing and reopening
// the drawer re-arms the gate exactly like a page navigation would.
const OrganizationDrawer = (props) => (
  <MfaStepUpGateProvider>
    <OrganizationDrawerInner {...props} />
  </MfaStepUpGateProvider>
);

export default OrganizationDrawer;
